import React, { FC, useState, useContext, useMemo, ReactNode, useRef, useEffect } from 'react'
import { Space, TableColumnProps, Tooltip, Skeleton, Checkbox, Button, Dropdown } from '@arco-design/web-react'
import { IconDragDotVertical, IconUnorderedList } from '@arco-design/web-react/icon'
import styles from './index.module.less'
import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useClickAway } from 'ahooks'
import useLocale from '@/utils/useLocale'
import { useLocalStorageState, useMount, useSetState } from 'ahooks'

interface IItem {
  dataIndex: string
  title: ReactNode
  isSelected?: boolean
}

interface IProps {
  defaultFields: IItem[]
  onOk: (list: IItem[]) => void
  onCancel?: () => void
  visible?: boolean
  id: string
}

interface IDropProps {
  defaultFields: IItem[]
  onOk: (list: IItem[]) => void
  onCancel?: () => void
  visible?: boolean
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  listeners?: SyntheticListenerMap
}

const RowContext = React.createContext<RowContextProps>({})

const DragHandle: FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext)
  return (
    <Button
      type="text"
      size="small"
      icon={<IconDragDotVertical />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  )
}

interface IRow {
  data: IItem
  dataIndex: string
  style?: React.CSSProperties
  onChange?: (c: boolean, data: IItem) => void
}
const Row: React.FC<IRow> = (props) => {
  const { dataIndex, data, onChange } = props
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: dataIndex
  })

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {})
  }

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  )

  return (
    <RowContext.Provider value={contextValue}>
      <div className={styles.dropItem} ref={setNodeRef} style={style} {...attributes}>
        <DragHandle />
        <Checkbox
          defaultChecked={data?.isSelected || false}
          onChange={(checked) => {
            onChange?.(checked, data)
          }}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {data.title}
        </Checkbox>
      </div>
    </RowContext.Provider>
  )
}

const DropList = ({ defaultFields, onOk, onCancel, visible }: IDropProps) => {
  const [dataSource, setDataSource] = useState(defaultFields || [])
  const ref = useRef<HTMLDivElement>(null)
  const t = useLocale()

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex((record) => record.dataIndex === active?.id)
        const overIndex = prevState.findIndex((record) => record.dataIndex === over?.id)
        return arrayMove(prevState, activeIndex, overIndex)
      })
    }
  }

  const handleChangeSelect = (checked: boolean, item: IItem) => {
    setDataSource((prevState) => {
      const newList = prevState.map((i) => (i.dataIndex === item.dataIndex ? { ...i, isSelected: checked } : i))
      return newList
    })
  }

  const handleSave = () => {
    onOk?.(dataSource)
    onCancel?.()
  }
  return (
    <div className={styles.customTableField} ref={ref}>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext items={dataSource.map((i) => i.dataIndex)} strategy={verticalListSortingStrategy}>
          <div className={styles.dropList}>
            {dataSource.map((item, index) => {
              return <Row dataIndex={item.dataIndex} data={item} key={index} onChange={handleChangeSelect} />
            })}
          </div>
        </SortableContext>
      </DndContext>
      <div className={styles.customFooter}>
        <Button onClick={() => onCancel?.()}>{t['modal.close']}</Button>
        <Button type="primary" onClick={handleSave}>
          {t['userSetting.btn.save']}
        </Button>
      </div>
    </div>
  )
}

const CustomField = ({ defaultFields, onOk, id }: IProps) => {
  const t = useLocale()
  const [visible, setVisible] = useState(false)
  const [localColumns, setLocalColumns] = useLocalStorageState<any[]>(id, {
    defaultValue: []
  })

  const finalColumns = useMemo(() => {
    return localColumns.map((column) => {
      return {
        dataIndex: column.dataIndex,
        isSelected: column.isSelected,
        title: defaultFields.find((d) => d.dataIndex === column.dataIndex)?.title
      }
    })
  }, [defaultFields, localColumns])

  useEffect(() => {
    if (localColumns?.length === 0 && defaultFields) {
      setLocalColumns(
        defaultFields.map((column) => {
          return {
            dataIndex: column.dataIndex,
            isSelected: column.isSelected
          }
        })
      )
    }
  }, [localColumns?.length, defaultFields])

  return (
    <Dropdown
      popupVisible={visible}
      droplist={
        <DropList
          defaultFields={finalColumns}
          onCancel={() => setVisible(false)}
          onOk={(list) => {
            onOk?.(list)
            setLocalColumns(
              list.map((column) => {
                return {
                  dataIndex: column.dataIndex,
                  isSelected: column.isSelected
                }
              })
            )
          }}
          visible={visible}
        />
      }
      position="bl"
    >
      <Button style={{ borderRadius: 8 }} type="outline" onClick={() => setVisible(true)} icon={<IconUnorderedList />}>
        {t['select_fields']}
      </Button>
    </Dropdown>
  )
}

export default CustomField
